/*  /frontend/src/context/AccountsContext.js
    Greatly Social – central cache for connected social accounts
    ----------------------------------------------------------------- */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { UserContext } from './UserContext'

/* ──────────────────────────────────────────────────────────── */

export const AccountsContext = createContext({
  accounts : [],
  loading  : true,
  refresh  : () => Promise.resolve(),
})

/* ----- local-storage helpers -------------------------------- */

const TTL_MS = 5 * 60 * 1_000           // 5-minute cache

const buildKey = userId => (userId ? `gs_cached_accounts::${userId}` : null)

function loadFromCache (userId) {
  const key = buildKey(userId)
  if (!key) return null
  try {
    const raw = JSON.parse(localStorage.getItem(key) || '{}')
    if (raw.ts && Date.now() - raw.ts < TTL_MS && Array.isArray(raw.data)) {
      return raw.data
    }
  } catch { /* ignore JSON errors */ }
  return null
}

function saveToCache (userId, data) {
  const key = buildKey(userId)
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }))
  } catch { /* ignore quota / storage errors */ }
}

function clearAllAccountCaches () {
  Object.keys(localStorage).forEach(k => {
    if (k.startsWith('gs_cached_accounts::')) localStorage.removeItem(k)
  })
}

/* ------------------------------------------------------------------ */

export function AccountsProvider ({ children }) {
  /* 1️⃣  who is logged in? */
  const { user, loading: userLoading } = useContext(UserContext)   // expects { id, … }
  const uid = user?.id || null

  /* 2️⃣  init from cache (if any) */
  const [accounts, setAccounts] = useState(() => loadFromCache(uid) || [])
  const [loading,  setLoading ] = useState(accounts.length === 0)

  /* 3️⃣  fetch fresh data from every provider */
  const fetchAccounts = useCallback(async () => {
    if (!uid) return                              // no user yet
    setLoading(true)

    try {
      const endpoints = {
        facebook : '/api/auth/facebook/pages/connected',
        instagram: '/api/auth/instagram/accounts',
        x        : '/api/auth/twitter/accounts',
        linkedin : '/api/auth/linkedin/accounts',
        threads  : '/api/auth/threads/accounts',
        tiktok   : '/api/auth/tiktok/accounts',
        pinterest: '/api/auth/pinterest/accounts',
        youtube  : '/api/auth/youtube/accounts',
      }

      /* hit every endpoint in parallel */
      const results = await Promise.allSettled(
        Object.entries(endpoints).map(async ([platform, url]) => {
          const res  = await fetch(url, { credentials: 'include' })
          const body = await res.json()
          return { platform, data: Array.isArray(body) ? body : [] }
        }),
      )

      /* ── normalise + flatten ── */
      const merged = []
      results.forEach(r => {
        if (r.status !== 'fulfilled') return
        const { platform, data } = r.value

        /* FACEBOOK: remap raw columns → generic keys the UI expects */
        if (platform === 'facebook') {
          data.forEach(row => {
            merged.push({
              platform   : 'facebook',

              /* unified keys */
              id         : row.fb_page_id,
              name       : row.fb_page_name,
              pictureUrl : row.fb_page_picture_url || '',

              /* extras used elsewhere */
              profileImage: row.fb_page_picture_url || '',
            })
          })
          return                                    // skip generic push
        }

        /* every other network keeps its existing field names */
        data.forEach(row => {
          merged.push({
            platform,
            ...row,
            profileImage:
              row.profileImage       ||
              row.igProfilePic       ||
              row.channelThumbnail   ||
              row.fb_page_picture_url||
              '',
          })
        })
      })

      setAccounts(merged)
      if (process.env.NODE_ENV !== 'production') {
 window.__GS_ACCOUNTS_DEBUG__ = merged;   // <-- add this line
}
      saveToCache(uid, merged)
    } finally {
      setLoading(false)
    }
  }, [uid])

  /* 4️⃣  first mount & user changes */
  useEffect(() => {
    if (userLoading) return                      // still resolving /me
    if (!uid) {
      clearAllAccountCaches()
      setAccounts([])
      setLoading(false)
      return
    }

    /* if we already have data for this user, keep it */
    if (accounts.length) return

    /* else fetch */
    fetchAccounts()
  }, [uid, userLoading, accounts.length, fetchAccounts])

  /* 5️⃣  expose context */
  const value = { accounts, loading, refresh: fetchAccounts }

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  )
}
